// client/src/coms/auth/providers/spotifyAuth.js
import { openAuthUrl, subscribeProviderAuthCallbacks } from '../providerAuth';
import {
  clearProviderSession,
  getProviderSession,
  setProviderSession,
} from '../providerSessions';

const PROVIDER = 'spotify';
const SPOTIFY_ACCOUNTS_AUTHORIZE_URL = 'https://accounts.spotify.com/authorize';
const SPOTIFY_ACCOUNTS_TOKEN_URL = 'https://accounts.spotify.com/api/token';
const SPOTIFY_ME_URL = 'https://api.spotify.com/v1/me';
const PENDING_KEY = 'vox_provider_auth_pending_spotify_v1';

const CLIENT_ID = String(process.env.REACT_APP_SPOTIFY_CLIENT_ID || '').trim();
const REDIRECT_URI = String(process.env.REACT_APP_SPOTIFY_REDIRECT_URI || 'voxemera-beta://spotify/callback').trim();
const SPOTIFY_SCOPES = [
  'streaming',
  'user-read-email',
  'user-read-private',
  'user-read-playback-state',
  'user-modify-playback-state',
  'user-read-currently-playing',
];

function nowMs() {
  return Date.now();
}

function safeJsonParse(value, fallback = null) {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function readPending() {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    const parsed = raw ? safeJsonParse(raw, null) : null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writePending(value) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(value));
  } catch {}
}

function clearPending() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {}
}

function base64UrlEncode(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function randomString(length = 64) {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  let out = '';
  for (let i = 0; i < array.length; i += 1) {
    out += charset[array[i] % charset.length];
  }
  return out;
}

async function sha256(value) {
  const encoded = new TextEncoder().encode(value);
  return crypto.subtle.digest('SHA-256', encoded);
}

async function createCodeChallenge(verifier) {
  const digest = await sha256(verifier);
  return base64UrlEncode(digest);
}

function normalizeScopeString(scopeValue) {
  return String(scopeValue || '')
    .trim()
    .split(/\s+/)
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .sort()
    .join(' ');
}

function scopeSet(scopeValue) {
  return new Set(
    String(scopeValue || '')
      .trim()
      .split(/\s+/)
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  );
}

function hasRequiredSpotifyScopes(scopeValue) {
  const scopes = scopeSet(scopeValue);
  return SPOTIFY_SCOPES.every((scope) => scopes.has(scope));
}

function isPremiumProduct(productValue) {
  return String(productValue || '').trim().toLowerCase() === 'premium';
}

function normalizeSpotifySession(raw) {
  const session = raw && typeof raw === 'object' ? raw : getProviderSession(PROVIDER);
  if (!session || typeof session !== 'object') return null;

  const accessToken = String(session.accessToken || '').trim();
  if (!accessToken) return null;

  return {
    provider: PROVIDER,
    status: 'connected',
    accessToken,
    refreshToken: String(session.refreshToken || '').trim(),
    tokenType: String(session.tokenType || 'Bearer').trim() || 'Bearer',
    scope: normalizeScopeString(session.scope || ''),
    expiresAt: Number(session.expiresAt) || 0,
    profile: session.profile && typeof session.profile === 'object' ? session.profile : null,
    updatedAt: Number(session.updatedAt) || nowMs(),
  };
}

async function fetchSpotifyProfile(accessToken) {
  try {
    const response = await fetch(SPOTIFY_ME_URL, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

function assertSpotifyWebPlaybackEligibility(session) {
  const normalized = normalizeSpotifySession(session);
  if (!normalized) {
    throw new Error('Spotify login required.');
  }

  if (!hasRequiredSpotifyScopes(normalized.scope)) {
    throw new Error('Spotify login is missing required playback scopes. Please disconnect Spotify and log in again.');
  }

  const product = String(normalized.profile?.product || '').trim().toLowerCase();
  if (product && !isPremiumProduct(product)) {
    throw new Error('Spotify Premium is required for full playback.');
  }

  return normalized;
}

async function exchangeCodeForToken(code, verifier) {
  const body = new URLSearchParams();
  body.set('client_id', CLIENT_ID);
  body.set('grant_type', 'authorization_code');
  body.set('code', code);
  body.set('redirect_uri', REDIRECT_URI);
  body.set('code_verifier', verifier);

  const response = await fetch(SPOTIFY_ACCOUNTS_TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Spotify token exchange failed.');
  }

  const data = await response.json();
  const accessToken = String(data.access_token || '').trim();
  if (!accessToken) {
    throw new Error('Spotify did not return an access token.');
  }

  const profile = await fetchSpotifyProfile(accessToken);
  const scope = normalizeScopeString(data.scope || '');

  const stored = setProviderSession(PROVIDER, {
    status: 'connected',
    accessToken,
    refreshToken: String(data.refresh_token || '').trim(),
    tokenType: String(data.token_type || 'Bearer').trim() || 'Bearer',
    scope,
    expiresAt: nowMs() + Math.max(0, (Number(data.expires_in) || 3600) * 1000) - 15000,
    profile,
    updatedAt: nowMs(),
  });

  clearPending();

  const normalized = normalizeSpotifySession(stored);
  console.log('SPOTIFY SESSION DEBUG', {
    scope: normalized?.scope || '',
    product: normalized?.profile?.product || '',
    spotifyId: normalized?.profile?.id || '',
    expiresAt: normalized?.expiresAt || 0,
  });

  return assertSpotifyWebPlaybackEligibility(normalized);
}

async function refreshSpotifyToken(session) {
  const current = normalizeSpotifySession(session);
  const refreshToken = String(current?.refreshToken || '').trim();
  if (!refreshToken) return current;

  const body = new URLSearchParams();
  body.set('client_id', CLIENT_ID);
  body.set('grant_type', 'refresh_token');
  body.set('refresh_token', refreshToken);

  const response = await fetch(SPOTIFY_ACCOUNTS_TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  });

  if (!response.ok) {
    clearSpotifySession();
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Spotify token refresh failed.');
  }

  const data = await response.json();
  const accessToken = String(data.access_token || '').trim();
  if (!accessToken) {
    clearSpotifySession();
    throw new Error('Spotify refresh returned no access token.');
  }

  const stored = setProviderSession(PROVIDER, {
    status: 'connected',
    accessToken,
    refreshToken: String(data.refresh_token || refreshToken).trim(),
    tokenType: String(data.token_type || current?.tokenType || 'Bearer').trim() || 'Bearer',
    scope: normalizeScopeString(data.scope || current?.scope || ''),
    expiresAt: nowMs() + Math.max(0, (Number(data.expires_in) || 3600) * 1000) - 15000,
    profile: current?.profile || null,
    updatedAt: nowMs(),
  });

  let normalized = normalizeSpotifySession(stored);

  const needsProfile =
    !normalized?.profile ||
    typeof normalized.profile !== 'object' ||
    !String(normalized.profile.product || '').trim();

  if (normalized && needsProfile) {
    const profile = await fetchSpotifyProfile(normalized.accessToken);
    normalized = normalizeSpotifySession(
      setProviderSession(PROVIDER, {
        ...normalized,
        profile,
        updatedAt: nowMs(),
      })
    );
  }

  console.log('SPOTIFY SESSION DEBUG', {
    scope: normalized?.scope || '',
    product: normalized?.profile?.product || '',
    spotifyId: normalized?.profile?.id || '',
    expiresAt: normalized?.expiresAt || 0,
  });

  return assertSpotifyWebPlaybackEligibility(normalized);
}

function tryParseSpotifyCallbackUrl(urlValue) {
  const raw = String(urlValue || '').trim();
  if (!raw) return null;

  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  const protocol = String(url.protocol || '').toLowerCase();
  if (protocol !== 'voxemera-beta:' && protocol !== 'voxemera:' && protocol !== 'pointatlas:') {
    return null;
  }

  const host = String(url.hostname || '').toLowerCase();
  const path = String(url.pathname || '').replace(/\/+$/g, '');
  if (host !== 'spotify' || path !== '/callback') {
    return null;
  }

  const error = String(url.searchParams.get('error') || '').trim();
  if (error) {
    throw new Error(`Spotify login failed: ${error}`);
  }

  const code = String(url.searchParams.get('code') || '').trim();
  const state = String(url.searchParams.get('state') || '').trim();
  if (!code || !state) return null;

  const pending = readPending();
  const verifier = String(pending?.verifier || '').trim();
  const expectedState = String(pending?.state || '').trim();

  if (!verifier || !expectedState || expectedState !== state) {
    throw new Error('Spotify login state mismatch.');
  }

  return {
    code,
    verifier,
  };
}

export function getSpotifySession() {
  return normalizeSpotifySession(getProviderSession(PROVIDER));
}

export function clearSpotifySession() {
  clearPending();
  clearProviderSession(PROVIDER);
}

export function spotifySessionSupportsWebPlayback(session) {
  try {
    const normalized = normalizeSpotifySession(session);
    if (!normalized) return false;
    if (!hasRequiredSpotifyScopes(normalized.scope)) return false;

    const product = String(normalized.profile?.product || '').trim().toLowerCase();
    if (product && !isPremiumProduct(product)) return false;

    return true;
  } catch {
    return false;
  }
}

export async function ensureFreshSpotifySession() {
  const session = getSpotifySession();
  if (!session) return null;

  if (session.expiresAt && session.expiresAt > nowMs() + 30000) {
    console.log('SPOTIFY SESSION DEBUG', {
      scope: session?.scope || '',
      product: session?.profile?.product || '',
      spotifyId: session?.profile?.id || '',
      expiresAt: session?.expiresAt || 0,
    });
    return assertSpotifyWebPlaybackEligibility(session);
  }

  return refreshSpotifyToken(session);
}

export async function refreshSpotifySessionIfNeeded(session) {
  const current = normalizeSpotifySession(session);
  if (!current) return null;

  if (current.expiresAt && current.expiresAt > nowMs() + 30000) {
    console.log('SPOTIFY SESSION DEBUG', {
      scope: current?.scope || '',
      product: current?.profile?.product || '',
      spotifyId: current?.profile?.id || '',
      expiresAt: current?.expiresAt || 0,
    });
    return assertSpotifyWebPlaybackEligibility(current);
  }

  return refreshSpotifyToken(current);
}

export async function startSpotifyLogin() {
  if (!CLIENT_ID || CLIENT_ID === 'your_spotify_client_id') {
    throw new Error('Missing REACT_APP_SPOTIFY_CLIENT_ID.');
  }

  const state = randomString(24);
  const verifier = randomString(96);
  const challenge = await createCodeChallenge(verifier);

  writePending({
    state,
    verifier,
    createdAt: nowMs(),
  });

  const params = new URLSearchParams();
  params.set('client_id', CLIENT_ID);
  params.set('response_type', 'code');
  params.set('redirect_uri', REDIRECT_URI);
  params.set('code_challenge_method', 'S256');
  params.set('code_challenge', challenge);
  params.set('state', state);
  params.set('scope', SPOTIFY_SCOPES.join(' '));
  params.set('show_dialog', 'true');

  const url = `${SPOTIFY_ACCOUNTS_AUTHORIZE_URL}?${params.toString()}`;
  const opened = await openAuthUrl(url, { provider: 'spotify' });
  if (!opened) {
    throw new Error('Could not start Spotify login.');
  }
}

export async function bindSpotifyDeepLinkAuth(onSession, onError) {
  return subscribeProviderAuthCallbacks(async (payload) => {
    try {
      const parsed = tryParseSpotifyCallbackUrl(payload && payload.url);
      if (!parsed) return;
      const session = await exchangeCodeForToken(parsed.code, parsed.verifier);
      if (typeof onSession === 'function') {
        onSession(session);
      }
    } catch (error) {
      if (typeof onError === 'function') {
        onError(error instanceof Error ? error : new Error('Spotify login failed.'));
      }
    }
  });
}